import * as React from 'react';
import { Popover } from 'antd';
import { observer } from 'mobx-react-lite';
import type { SectionDetail, SectionDetails } from '../../misc/SectionDetails';
import './SectionDetailsPopover.less';

interface Props {
    sectionName: string;
    description?: string | null;
    getDetails: () => SectionDetails;
}

const SectionDetailsPopover: React.FC<Props> = ({ sectionName, description, getDetails }) => {
    const [visible, setVisible] = React.useState(false);
    const trigger = React.useRef<HTMLButtonElement>(null);
    const content = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!visible) {
            return;
        }

        const dismissOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setVisible(false);
                trigger.current?.focus();
            }
        };

        document.addEventListener('keydown', dismissOnEscape, true);
        return () => document.removeEventListener('keydown', dismissOnEscape, true);
    }, [visible]);

    return (
        <span className="section-details-trigger">
            <Popover
                trigger="click"
                placement="bottomRight"
                title="Section details"
                visible={visible}
                onVisibleChange={setVisible}
                afterVisibleChange={opened => {
                    if (opened) {
                        content.current?.focus();
                    }
                }}
                getPopupContainer={node => node.parentElement ?? document.body}
                destroyTooltipOnHide
                content={
                    <SectionDetailsContent
                        contentRef={content}
                        sectionName={sectionName}
                        description={description}
                        getDetails={getDetails}
                    />
                }
            >
                <button
                    ref={trigger}
                    className="section-details-link"
                    type="button"
                    aria-haspopup="dialog"
                    aria-expanded={visible}
                    aria-label={`Section details for ${sectionName}`}
                >
                    Section details
                </button>
            </Popover>
        </span>
    );
};

const SectionDetailsContent = observer(
    ({
        sectionName,
        description,
        getDetails,
        contentRef
    }: Props & {
        contentRef: React.RefObject<HTMLDivElement>;
    }) => {
        const details = getDetails();
        const renderDetails = (items: SectionDetail[]) => (
            <dl>
                {items.map(item => (
                    <React.Fragment key={item.id}>
                        <dt>{item.label}</dt>
                        <dd>{item.value}</dd>
                    </React.Fragment>
                ))}
            </dl>
        );

        return (
            <div
                ref={contentRef}
                className="section-details-popover"
                role="dialog"
                aria-label={`Section information for ${sectionName}`}
                tabIndex={-1}
            >
                <dl>
                    <dt>Section name</dt>
                    <dd>{sectionName.trim() || 'Not specified'}</dd>
                    <dt>Description</dt>
                    <dd>{description?.trim() || 'Not specified'}</dd>
                </dl>
                <h4>Classifications</h4>
                {renderDetails(details.classifications)}
                <h4>Territories</h4>
                {renderDetails(details.territories)}
            </div>
        );
    }
);

export default SectionDetailsPopover;
